/**
 * Observer calendar / notification → /media-vision handoff (Aşama 19).
 * sessionId is never invented; AssignedStudiesPanel probe rules are the authority.
 */

export const MEDIA_VISION_PATH = "/media-vision";

export const OBSERVER_CALENDAR_EVENT_TYPE = "observer_task";

export const OBSERVER_NOTIFICATION_TYPES = Object.freeze([
  "observer_round_opened",
  "observer_round_schedule_updated",
  "observer_study_invitation",
  "observer_study_terminated",
]);

export const OBSERVER_HANDOFF_ACTIONS = Object.freeze({
  OPEN_SESSION: "openSession",
  OPEN_ASSIGNED_LIST: "openAssignedList",
  OPEN_ANALYSIS: "openAnalysis",
  DENY: "deny",
});

export const OBSERVER_HANDOFF_MESSAGES = Object.freeze({
  DENIED: "Bu çalışmada gözlemci olarak kayıtlı değilsiniz.",
  UNAVAILABLE: "Ölçüm görevleri şu anda okunamıyor. Lütfen tekrar deneyin.",
  INVALID: "Çalışma bilgisi okunamadı.",
});

export function getHandoffMessage(code, t) {
  const key = OBSERVER_HANDOFF_MESSAGES[code] ? code : "INVALID";
  const fallback = OBSERVER_HANDOFF_MESSAGES[key];
  return typeof t === "function" ? t(`handoff.${key}`, { defaultValue: fallback }) : fallback;
}

const ANALYSIS_ONLY_STATUSES = Object.freeze(["locked", "archived", "ready_for_analysis"]);

function isStrictId(value) {
  return typeof value === "string" && value.trim() !== "";
}

function asRoundNumber(value) {
  if (typeof value === "number" && Number.isInteger(value) && value >= 1) return value;
  if (typeof value === "string" && /^(?:0|[1-9]\d*)$/.test(value)) {
    const n = Number(value);
    if (Number.isInteger(n) && n >= 1) return n;
  }
  return null;
}

function eventTypeOf(event) {
  if (!event || typeof event !== "object") return null;
  return event.type || event.extendedProps?.type || null;
}

export function isObserverCalendarEvent(event) {
  return eventTypeOf(event) === OBSERVER_CALENDAR_EVENT_TYPE;
}

export function isObserverNotificationType(type) {
  return typeof type === "string" && OBSERVER_NOTIFICATION_TYPES.includes(type);
}

export function getObserverCalendarEffectiveStatus(event) {
  const props = event?.extendedProps && typeof event.extendedProps === "object"
    ? event.extendedProps
    : {};
  if (props.isCompleted === true || props.status === "completed") return "completed";
  if (props.status === "planned") return "planned";
  if (props.status === "pending") return "pending";
  if (typeof props.status === "string" && props.status.trim() !== "") return props.status;
  return "pending";
}

export function getCalendarEventRoleLabel(event, t) {
  const translate = typeof t === "function" ? t : null;
  if (isObserverCalendarEvent(event)) {
    return translate ? translate("roles.forObserverStudy", "[Gözlemci Görevi]") : "[Gözlemci Görevi]";
  }
  const isForMe = Boolean(event?.extendedProps?.isForMe);
  if (isForMe) {
    return translate ? translate("roles.forDoctor", "[Doktor İçin]") : "[Doktor İçin]";
  }
  const type = eventTypeOf(event);
  if (type && type !== "custom" && !String(type).startsWith("custom")) {
    return translate ? translate("roles.forPatient", "[Hasta İçin]") : "[Hasta İçin]";
  }
  return "";
}

export function buildObserverStudyIntentFromCalendar(event) {
  if (!isObserverCalendarEvent(event)) return null;
  const props = event.extendedProps && typeof event.extendedProps === "object"
    ? event.extendedProps
    : {};
  const studyId = isStrictId(props.studyId) ? props.studyId : null;
  if (!studyId) return null;
  return {
    studyId,
    roundNumber: asRoundNumber(props.roundNumber),
    status: getObserverCalendarEffectiveStatus(event),
    source: "calendar",
  };
}

export function buildObserverStudyIntentFromNotification(notif) {
  if (!notif || typeof notif !== "object") return null;
  if (!isObserverNotificationType(notif.type)) return null;
  const data = notif.data && typeof notif.data === "object" ? notif.data : {};
  const studyId = isStrictId(data.studyId) ? data.studyId : null;
  if (!studyId) return null;
  let status = null;
  if (notif.type === "observer_round_opened") status = "pending";
  if (notif.type === "observer_round_schedule_updated") status = "planned";
  if (notif.type === "observer_study_invitation") status = "invited";
  if (notif.type === "observer_study_terminated") status = "terminated";
  return {
    studyId,
    roundNumber: asRoundNumber(data.roundNumber),
    status,
    source: "notification",
    notificationType: notif.type,
  };
}

export function buildObserverStudyIntentFromSearch(search) {
  const raw = typeof search === "string" ? search : "";
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const studyId = params.get("observerStudy");
  if (!isStrictId(studyId)) return null;
  return {
    studyId,
    roundNumber: asRoundNumber(params.get("round") || params.get("roundNumber")),
    status: null,
    source: "search",
  };
}

export function readObserverStudyIntentFromLocation(location) {
  const fromState = location?.state?.observerStudyIntent;
  if (fromState && isStrictId(fromState.studyId)) {
    return {
      studyId: fromState.studyId,
      roundNumber: asRoundNumber(fromState.roundNumber),
      status: typeof fromState.status === "string" ? fromState.status : null,
      source: fromState.source || "state",
      notificationType: fromState.notificationType || null,
    };
  }
  return buildObserverStudyIntentFromSearch(location?.search);
}

export function buildObserverStudyLocation(intent) {
  if (!intent || !isStrictId(intent.studyId)) return null;
  return {
    pathname: MEDIA_VISION_PATH,
    state: { observerStudyIntent: intent },
  };
}

export function stripObserverStudyHandoff(location) {
  const prevState = location?.state && typeof location.state === "object" && !Array.isArray(location.state)
    ? { ...location.state }
    : {};
  delete prevState.observerStudyIntent;
  const params = new URLSearchParams(location?.search || "");
  params.delete("observerStudy");
  params.delete("round");
  params.delete("roundNumber");
  const search = params.toString();
  return {
    pathname: location?.pathname || MEDIA_VISION_PATH,
    search: search ? `?${search}` : "",
    state: Object.keys(prevState).length > 0 ? prevState : {},
  };
}

function studyKindOf(study) {
  const kind = study?.studyKind || study?.study_kind;
  return kind === "annotation_agreement" ? "annotation_agreement" : "measurement";
}

export function decideObserverHandoff({
  intent,
  assignedStudy,
  assignmentResponse,
  assignmentError,
} = {}) {
  if (!intent || !isStrictId(intent.studyId)) {
    return {
      action: OBSERVER_HANDOFF_ACTIONS.DENY,
      snackbar: OBSERVER_HANDOFF_MESSAGES.INVALID,
      snackbarCode: "INVALID",
      openAssignedList: false,
    };
  }

  if (!assignedStudy || assignedStudy.id !== intent.studyId) {
    return {
      action: OBSERVER_HANDOFF_ACTIONS.DENY,
      snackbar: OBSERVER_HANDOFF_MESSAGES.DENIED,
      snackbarCode: "DENIED",
      openAssignedList: true,
    };
  }

  if (assignedStudy.invitationPending === true || intent.status === "invited") {
    return { action: OBSERVER_HANDOFF_ACTIONS.OPEN_ASSIGNED_LIST };
  }

  if (ANALYSIS_ONLY_STATUSES.includes(assignedStudy.status)) {
    return {
      action: OBSERVER_HANDOFF_ACTIONS.OPEN_ANALYSIS,
      studyId: assignedStudy.id,
      studyTitle: assignedStudy.title || null,
      studyStatus: assignedStudy.status,
      studyKind: studyKindOf(assignedStudy),
    };
  }

  if (intent.status === "planned" || intent.status === "completed") {
    return { action: OBSERVER_HANDOFF_ACTIONS.OPEN_ASSIGNED_LIST };
  }

  if (assignmentError) {
    const code = assignmentError.code;
    const denied = code === "OBSERVER_ACCESS_DENIED" || code === "STUDY_NOT_AVAILABLE";
    return {
      action: OBSERVER_HANDOFF_ACTIONS.DENY,
      snackbar: denied ? OBSERVER_HANDOFF_MESSAGES.DENIED : OBSERVER_HANDOFF_MESSAGES.UNAVAILABLE,
      snackbarCode: denied ? "DENIED" : "UNAVAILABLE",
      openAssignedList: true,
    };
  }

  const selection = assignmentResponse?.selection ?? null;
  const sessionId = selection && isStrictId(selection.sessionId) ? selection.sessionId : null;
  if (sessionId) {
    return {
      action: OBSERVER_HANDOFF_ACTIONS.OPEN_SESSION,
      studyId: assignedStudy.id,
      sessionId,
      studyKind: studyKindOf(assignedStudy),
      roundProgress: assignmentResponse.roundProgress ?? null,
    };
  }

  return { action: OBSERVER_HANDOFF_ACTIONS.OPEN_ASSIGNED_LIST };
}
